<script setup lang="ts">
defineOptions({
  name: "CartPage",
});
definePageMeta({ layout: "checkout" });

const { cartItems, subtotal, totalPrice, shippingTotal } = useCart();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const hasItems = computed(() => cartItems.value.length > 0);
</script>

<template>
  <div class="cart-page">
    <div v-if="hasItems" class="my-shoping-cart px-4">
      <div class="container mx-auto relative py-12 sm:py-20">
        <div class="cart-wrap flex justify-between flex-wrap md:flex-nowrap">
          <div
            class="cart w-full md:w-[450px] lg:w-[550px] xl:w-[692px] mb-5 md:mb-0"
          >
            <div class="title mb-8">
              <h2
                class="text-2xl md:text-3xl lg:text-4xl font-bold text-dark-bg"
              >
                {{ $t("cart.header") }} ({{ cartItems.length }})
              </h2>
            </div>

            <div class="cart-list">
              <div
                class="list flex py-8 border-y border-custom-grey"
                v-for="cartItem in cartItems"
                :key="cartItem.id"
              >
                <CheckoutCartItem :cart-item="cartItem" />
              </div>
            </div>
          </div>

          <div class="summary w-full md:w-[250px] lg:w-[300px] xl:w-[387px]">
            <div class="summary-inner sticky top-10">
              <!-- Summary Title -->
              <h2 class="text-[24px] font-bold text-dark-bg mb-6">
                {{ $t("cart.orderSummary") }}
              </h2>

              <CheckoutPromotionCode />

              <!-- Separator -->
              <hr class="border border-custom-grey my-8" />

              <p></p>

              <!-- Subtotal and Estimated Shipping -->
              <div class="text-sm sm:text-base text-dark-bg">

                <div class="flex justify-between items-center">
                  <span>{{ $t("cart.shippingEstimate") }}</span>
                  <span>
                    <SharedPrice
                      :value="shippingTotal"
                      class="text-secondary-900 font-medium"
                      data-testid="cart-subtotal"
                    />
                  </span>
                </div>
                <p></p>
                <div class="flex justify-between items-center mt-4">
                  <span>{{ $t("cart.subtotal") }}</span>
                  <span
                    ><SharedPrice
                      :value="subtotal"
                      class="text-secondary-900 font-medium"
                      data-testid="cart-subtotal"
                  /></span>
                </div>
                

               
              </div>

              <!-- Separator -->
              <hr class="border border-custom-grey my-6" />

              <!-- Total -->
              <div
                class="flex justify-between items-center mb-6 text-sm sm:text-base text-dark-bg font-bold"
              >
                <span>{{ $t("cart.orderTotal") }}</span>
                <SharedPrice :value="totalPrice" data-testid="cart-subtotal" />
              </div>

              <NuxtLink
                class="block text-center bg-red-bg text-white text-sm md:text-base font-bold py-3 px-6 rounded-full hover:bg-chinese-black transition duration-300 w-full"
                data-testid="cart-checkout-link"
                :to="formatLink(`/checkout`)"
              >
                {{ $t("checkout.title") }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col items-center justify-center py-20 px-4 max-w-2xl mx-auto text-center"
    >
      <div class="w-32 h-32 i-carbon-shopping-cart text-primary/30 mb-8" />
      <h1 class="text-3xl font-extrabold mb-4 text-secondary-900">
        {{ $t("cart.emptyCartLabel") }}
      </h1>
      <p class="text-lg text-secondary-600 mb-8">
        {{ $t("cart.emptyCartText") }}
      </p>
      <NuxtLink
        :to="formatLink(`/`)"
        class="inline-flex items-center gap-2 px-6 py-3 border border-transparent rounded-full shadow-sm text-base font-medium text-white bg-primary hover:bg-primary/90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        data-testid="cart-empty-continue-link"
      >
        <div class="i-carbon-shopping-bag w-5 h-5" />
        {{ $t("cart.continueShopping") }}
      </NuxtLink>
    </div>
  </div>
</template>
